/* eslint-disable react/jsx-props-no-spreading */
import _ from 'lodash';
import React, {FC, ReactNode, useCallback, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useBuildCancelToken} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {FrontEvent, Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  ExtendedError,
  UnreachableError,
} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  CancelError,
  CancelToken,
} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  HttpNetworkError,
  HttpTimeoutError,
} from '../../../../../libs/shared/core/src/services/http/httpError';
import {SchedulingLinksAccessPointsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  CalendarsPanelId,
  ComposerPanelId,
  makeCalendarsPanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownLoader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {DropdownItemSeparator} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemSeparator';
import {SearchDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {InteractiveListItem} from '../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {filterBySearchQuery} from '../../../../helpers/models/schedulingLinkHelpers';
import {SchedulingLinkModel} from '../../../../models/api/schedulingLinkModel';
import {SchedulingLinksListItemContainer} from '../../../calendars/scheduling/schedulingLinksListItemContainer';
import {SchedulingLinkSettingsDialogContainer} from '../../../calendars/scheduling/settings/schedulingLinkSettingsDialogContainer';
import {AddSchedulingLinkDropdownItem} from './addSchedulingLinkDropdownItem';
import {MeetingTimesData} from './meetingsettings/meetingSettingsPanel';
import {SchedulingLinkInsertSlotsDropdown} from './schedulingLinkInsertSlotsDropdown';
import {SchedulingLinksEmptyDropdown} from './schedulingLinksEmptyDropdown';

/*
 * Types.
 */

enum DropdownItemTypesEnum {
  HEADER = 'HEADER',
  CREATE = 'CREATE',
  SCHEDULING_LINK = 'SCHEDULING_LINK',
}

interface DropdownItemHeader {
  type: DropdownItemTypesEnum.HEADER;
  title: Renderer;
}

interface DropdownItemCreate {
  type: DropdownItemTypesEnum.CREATE;
}

interface DropdownItemSchedulingLink {
  type: DropdownItemTypesEnum.SCHEDULING_LINK;
  schedulingLink: SchedulingLinkModel;
}

type SchedulingDropdownItem = DropdownItemHeader | DropdownItemCreate | DropdownItemSchedulingLink;

/*
 * Props.
 */

export interface SchedulingLinksDropdownProps extends DropdownProps {
  panelId: ComposerPanelId | ViewerPanelId;
  schedulingLinks: ReadonlyArray<SchedulingLinkModel>;
  fetchSchedulingLinksAsync: (cancelToken: CancelToken) => Promise<void>;
  backButtonLabel: ReactNode;
  onInsertMeetingTimes: (data: MeetingTimesData) => void;
  onSchedulingLinkInsertLink: (model: SchedulingLinkModel) => void;
  onBackButtonClick: () => void;
  isQuickInsert: boolean;
}

/*
 * Constants.
 */

const dropdownHeader = <Trans id="schedulinglinks-dropdown-header">Scheduling links</Trans>;
const dropdownHeight = 470;
const dropdownWidth = 320;

/*
 * Styles.
 */

const StyledLoaderDiv = styled.div`
  height: 100%;
  ${center()};
`;

interface SchedulingLinkItemWrapperStyleProps {
  $isHighlighted: boolean;
}

const StyledSchedulingLinkItemWrapperDiv = styled.div<SchedulingLinkItemWrapperStyleProps>`
  > div {
    padding: 8px 12px;
  }

  ${(p) => maybeHighlightedStyle(p.$isHighlighted)}
`;

function maybeHighlightedStyle(isHighlighted: boolean) {
  if (!isHighlighted) {
    return '';
  }

  return css`
    background-color: ${(p) => p.theme.alphas.gray10};
  `;
}

/*
 * Component.
 */

export const SchedulingLinksDropdown: FC<SchedulingLinksDropdownProps> = (props) => {
  const {
    panelId,
    schedulingLinks,
    isQuickInsert,
    onSchedulingLinkInsertLink,
    fetchSchedulingLinksAsync,
    onInsertMeetingTimes,
    ...dropdownProps
  } = props;

  const [status, setStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);
  const [newlyCreatedSchedulingLinkId, setNewlyCreatedSchedulingLinkId] = useState<number>();
  const [meetingTimesDataToInsert, setMeetingTimesDataToInsert] = useState<MeetingTimesData>();
  const {buildCancelToken} = useBuildCancelToken();

  const [newLinkDialogPanelId, setNewLinkDialogPanelId] = useState<CalendarsPanelId>();
  const onNewLinkClick = () => setNewLinkDialogPanelId(makeCalendarsPanelId());
  const onNewLinkDialogClose = () => setNewLinkDialogPanelId(undefined);

  const [query, setQuery] = useState('');
  const data = createDropdownItems(schedulingLinks, isQuickInsert);
  const filteredData = filterDropdownItems(data, query);

  const tryFetchSchedulingLinksAsync = async () => {
    setStatus(AsyncStatusesEnum.LOADING);
    try {
      await fetchSchedulingLinksAsync(buildCancelToken());
      setStatus(AsyncStatusesEnum.LOADED);
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      setStatus(AsyncStatusesEnum.FAILED);

      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        return;
      }

      reportToBugsnag(new ExtendedError('Failed to fetch scheduling links list.', error));
    }
  };

  useInitialEffect(() => {
    tryFetchSchedulingLinksAsync();
  });

  const onAddSchedulingLinkClick = useCallback((e: FrontEvent) => {
    // Don't close the dropdown.
    e.preventDefault();
    // Open the schedulingLink settings dialog.
    onNewLinkClick();
  }, []);

  const renderRow = useCallback(
    (item: SchedulingDropdownItem, index: number) => {
      switch (item.type) {
        case DropdownItemTypesEnum.HEADER:
          return <DropdownCategory>{item.title()}</DropdownCategory>;

        case DropdownItemTypesEnum.CREATE:
          return (
            <>
              <AddSchedulingLinkDropdownItem onSelect={onAddSchedulingLinkClick} index={index} />
              <DropdownItemSeparator />
            </>
          );

        case DropdownItemTypesEnum.SCHEDULING_LINK:
          return (
            <InteractiveListItem
              onSelect={() => isQuickInsert && onSchedulingLinkInsertLink(item.schedulingLink)}
              index={index}
            >
              {({isHighlighted, itemProps}) => (
                <StyledSchedulingLinkItemWrapperDiv
                  $isHighlighted={isHighlighted}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(isQuickInsert && itemProps)}
                >
                  <SchedulingLinksListItemContainer
                    key={item.schedulingLink.id}
                    panelId={panelId}
                    schedulingLink={item.schedulingLink}
                    shouldShowInsertMeetingButtons={!isQuickInsert}
                    onInsertMeetingTimes={setMeetingTimesDataToInsert}
                    onInsertLinkClick={() => onSchedulingLinkInsertLink(item.schedulingLink)}
                    isNewlyCreated={item.schedulingLink.id === newlyCreatedSchedulingLinkId}
                    accessPoint={SchedulingLinksAccessPointsEnum.COMPOSER}
                    isQuickInsert={isQuickInsert}
                    // Interactions with the dialogs exposed by the "more" menu
                    // propagate up to `InteractiveListItem` and cause
                    // `onSelect()` to be called erroneously. Given how these
                    // dialogs are rendered and the way events are handled, there
                    // is no simple way to distinguish between these interactions.
                    // Hide the "more" menu for now.
                    shouldHideMoreMenu={isQuickInsert}
                  />
                </StyledSchedulingLinkItemWrapperDiv>
              )}
            </InteractiveListItem>
          );

        default:
          throw new UnreachableError(item);
      }
    },
    [
      isQuickInsert,
      newlyCreatedSchedulingLinkId,
      onAddSchedulingLinkClick,
      onSchedulingLinkInsertLink,
      panelId,
    ],
  );
  const findRowHeight = useCallback(
    (item: SchedulingDropdownItem) => {
      switch (item?.type) {
        case DropdownItemTypesEnum.HEADER:
          return 30;

        case DropdownItemTypesEnum.CREATE:
          return 40;

        case DropdownItemTypesEnum.SCHEDULING_LINK:
          return isQuickInsert ? 55 : 120;

        default:
          return 0;
      }
    },
    [isQuickInsert],
  );

  // If schedulingLinks haven't been fetched yet, render a loader.
  if (status !== AsyncStatusesEnum.LOADED) {
    return (
      <Dropdown {...dropdownProps} height={dropdownHeight} width={dropdownWidth}>
        <DropdownHeader title={dropdownHeader} />
        <StyledLoaderDiv>
          <DropdownLoader status={status} onRetryClick={tryFetchSchedulingLinksAsync} />
        </StyledLoaderDiv>
      </Dropdown>
    );
  }

  // If we're in the process of inserting meeting slots from a scheduling link, show the formatting settings.
  if (meetingTimesDataToInsert) {
    const schedulingLinkToInsert = schedulingLinks.find((l) => l.id === meetingTimesDataToInsert.linkId);
    if (!schedulingLinkToInsert) {
      return null;
    }

    return (
      <SchedulingLinkInsertSlotsDropdown
        {...dropdownProps}
        panelId={panelId}
        meetingTimesData={meetingTimesDataToInsert}
        schedulingLink={schedulingLinkToInsert}
        onMeetingTimesDataChange={setMeetingTimesDataToInsert}
        onInsertMeetingTimes={onInsertMeetingTimes}
        onBackButtonClick={() => setMeetingTimesDataToInsert(undefined)}
      />
    );
  }

  return (
    <>
      {schedulingLinks.length === 0 && (
        <SchedulingLinksEmptyDropdown
          {...dropdownProps}
          title={dropdownHeader}
          height={dropdownHeight}
          width={dropdownWidth}
          onAddSchedulingLinkClick={onAddSchedulingLinkClick}
        />
      )}
      {schedulingLinks.length > 0 && (
        <SearchDropdown
          {...dropdownProps}
          headerTitle={dropdownHeader}
          data={filteredData}
          height={dropdownHeight}
          width={dropdownWidth}
          query={query}
          onQueryChange={setQuery}
          renderRow={renderRow}
          findRowHeight={findRowHeight}
          defaultHighlightedIndex={isQuickInsert ? 1 : undefined}
        />
      )}
      {newLinkDialogPanelId && (
        <SchedulingLinkSettingsDialogContainer
          panelId={newLinkDialogPanelId}
          parentPanelId={panelId}
          onDialogClose={onNewLinkDialogClose}
          onSchedulingLinkCreated={setNewlyCreatedSchedulingLinkId}
          accessPoint={SchedulingLinksAccessPointsEnum.COMPOSER}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

const createDropdownItems = (
  schedulingLinks: ReadonlyArray<SchedulingLinkModel>,
  isQuickInsert: boolean,
): ReadonlyArray<SchedulingDropdownItem> => {
  const items: Array<SchedulingDropdownItem> = [];

  if (!isQuickInsert) {
    items.push(
      {
        type: DropdownItemTypesEnum.HEADER,
        title: () => <Trans id="dropdown-schedulinglinks-section-create">Create</Trans>,
      },
      {
        type: DropdownItemTypesEnum.CREATE,
      },
    );
  }

  items.push({
    type: DropdownItemTypesEnum.HEADER,
    title: () => <Trans id="dropdown-schedulinglinks-section-all">All</Trans>,
  });

  for (const link of schedulingLinks) {
    items.push({
      type: DropdownItemTypesEnum.SCHEDULING_LINK,
      schedulingLink: link,
    });
  }

  return items;
};

const filterDropdownItems = (items: ReadonlyArray<SchedulingDropdownItem>, query: string) => {
  if (!query) {
    return items;
  }

  const schedulingLinksToConsider = _(items)
    .map((item) => (item.type === DropdownItemTypesEnum.SCHEDULING_LINK ? item.schedulingLink : undefined))
    .compact()
    .value();

  const schedulingLinksToReturn = filterBySearchQuery(schedulingLinksToConsider, query);
  const schedulingLinkIdsToReturn = new Set(schedulingLinksToReturn.map((link) => link.id));

  return items.filter(
    (item) =>
      item.type === DropdownItemTypesEnum.SCHEDULING_LINK &&
      schedulingLinkIdsToReturn.has(item.schedulingLink.id),
  );
};
